import React from 'react';
import classNames from 'classnames';
import styled from 'styled-components';
import AsyncAutocomplete, { AsyncAutocompleteProps } from '../async-autocomplete';
import ProgressBar from '../../components/progress-bar';
import { Div, Flex } from '../../components/box';
import Tag, { TagProps } from '../../components/tag';
import Text from '../../components/text';
import { InputProps } from '../../components/input';
import { AutocompleteItem, AutocompleteProps, dropdownSizes } from '../autocomplete/autocomplete';
import { spacing } from '../../style';
import { variants } from './style';

export interface ApiClientOptions {
  method?: 'GET' | 'POST' | 'DELETE' | 'PATCH' | 'PUT';
  headers?: any;
  withApiHost?: boolean;
  body?: string | FormData | File;
  credentials?: RequestCredentials;
}

export const defaultTagsInputVariant = 'input';

export type TApiAuthClient = {
  headers: Record<string, string | undefined>;
  setAccessToken: (token: string) => void;
  fetch: (endpoint: string, options?: ApiClientOptions | undefined) => Promise<Response>;
};

export interface TagsInputProps<T = AutocompleteItem> {
  values?: T[];
  variant?: keyof typeof variants;
  maxAllowed?: number;
  apiAuthClient: TApiAuthClient;
  onRemove?: (option: T, index: number) => void;
  className?: string;
  selectedLabelProps?: TagProps;
  disableProgressBar?: boolean;
  renderLabel?: ({
    item,
    onRemove,
    disabled,
  }: {
    disabled?: boolean;
    item: T;
    onRemove: () => void;
  }) => React.ReactElement;
  renderTag?: ({
    item,
    onRemove,
    disabled,
    index,
  }: {
    disabled?: boolean;
    item: T;
    onRemove: () => void;
    index: number;
  }) => React.ReactNode;
  inputComponent?: React.ElementType;
  autoCompleteType?: AsyncAutocompleteProps<T>['type'];
  inputProps?: InputProps;
  placeholder?: string;
  disabled?: boolean;
  inputType?: 'input' | 'autocomplete';
  autoCompleteItems?: T[];
  autocompleteQuery?: AsyncAutocompleteProps<T>['autocompleteQuery'];
  autoCompleteFetchOptions?: AsyncAutocompleteProps<T>['autoCompleteFetchOptions'];
  autocompleteUrl?: string;
  autocompleteQueryKey?: string;
  extractAutocompleteListFromResponse?: AsyncAutocompleteProps<T>['extractListFromResponse'];
  renderAutoCompleteItem?: AutocompleteProps<T>['renderItem'];
  parseSelectedLabel?: (option: T) => React.ReactElement | string | undefined;
  maxAllowedMessageText?: string;
  autoCompleteMeta?: any;
  makeItemFromInputValue?: AutocompleteProps<T>['makeItemFromInputValue'];
  allowOpenValue?: AutocompleteProps<T>['allowOpenValue'];
  allowOpenValueAsItem?: AutocompleteProps<T>['allowOpenValueAsItem'];
  shouldScrollToView?: AutocompleteProps<T>['shouldScrollToView'];
  'data-name'?: string;
  'aria-required'?: string;
  id?: string | number;
  onAdd?: AutocompleteProps<T>['onSelect'];
  onSelectWhenInputIsNotAutoComplete?: (value: string) => void;
  dropdownHeight?: keyof typeof dropdownSizes;
  filterAutocompleteItems?: AutocompleteProps<T>['filterItems'];
  allowRemoveTagOnBackspaceWhenInputIsEmpty?: boolean;
  itemToString?: AutocompleteProps<T>['itemToString'];
}

const TagsInputComponent = <T = AutocompleteItem,>({
  values = [],
  variant = defaultTagsInputVariant,
  apiAuthClient,
  maxAllowed,
  onRemove,
  onSelectWhenInputIsNotAutoComplete,
  className,
  selectedLabelProps = {},
  disableProgressBar,
  renderLabel,
  renderTag,
  inputComponent: InputComponent = AsyncAutocomplete,
  autoCompleteType,
  onAdd,
  inputProps,
  placeholder,
  disabled,
  inputType = 'autocomplete',
  autoCompleteItems,
  makeItemFromInputValue,
  autocompleteQuery,
  renderAutoCompleteItem,
  //@ts-expect-error
  parseSelectedLabel = (option: T) => (typeof option === 'string' ? option : option?.name),
  maxAllowedMessageText = '',
  'data-name': dataName,
  'aria-required': ariaRequired,
  autoCompleteMeta,
  allowOpenValue,
  id,
  allowOpenValueAsItem,
  shouldScrollToView,
  dropdownHeight,
  filterAutocompleteItems,
  autoCompleteFetchOptions,
  autocompleteUrl,
  extractAutocompleteListFromResponse,
  autocompleteQueryKey,
  allowRemoveTagOnBackspaceWhenInputIsEmpty = false,
  itemToString,
}: TagsInputProps<T>) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');

  const onInputSelect = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const target = e.target as HTMLInputElement;
      onSelectWhenInputIsNotAutoComplete?.(target.value);
      setInputValue('');
    }
  };

  const { Container, inputProps: variantInputProps, growInput } = variants[variant || defaultTagsInputVariant] || {};
  const hasReachedMaxAmount = maxAllowed ? maxAllowed <= values?.length : false;

  const inputProperties = {
    'aria-required': ariaRequired,
    disableClear: true,
    placeholder,
    disabled: hasReachedMaxAmount || disabled,
    className: classNames(inputProps?.className, 'tags-input-input'),
    ...inputProps,
    ...variantInputProps,
  };

  return (
    <Div data-name={dataName} className={classNames(className, 'box-tags-input', { 'grow-input': growInput })}>
      <Container className={classNames('input-container', { focus: isFocused })} ariaLabel={placeholder}>
        <Flex maxWidth={'100%'} flexGrow={1}>
          <Flex flexWrap={'wrap'} className={'group-container'} role={'group'}>
            {Object.values(values || []).map((option, index) => {
              return renderTag ? (
                <React.Fragment key={index}>
                  {renderTag({
                    item: option,
                    disabled,
                    onRemove: () => onRemove?.(option, index),
                    index,
                  })}
                </React.Fragment>
              ) : (
                <Tag
                  key={index}
                  disabled={disabled}
                  data-name={'tags-input-selected-label'}
                  size={'lg'}
                  variant={'gray900'}
                  {...selectedLabelProps}
                >
                  {renderLabel ? (
                    renderLabel({
                      item: option,
                      disabled,
                      onRemove: () => onRemove?.(option, index),
                    })
                  ) : (
                    <React.Fragment>
                      <Tag.Text maxWidth={selectedLabelProps?.maxWidth}>{parseSelectedLabel?.(option)}</Tag.Text>
                      <Tag.ActionIcon
                        aria-label={`Remove ${parseSelectedLabel?.(option)}`}
                        disabled={disabled}
                        placement={'right'}
                        onClick={() => !disabled && onRemove?.(option, index)}
                      />
                    </React.Fragment>
                  )}
                </Tag>
              );
            })}
            {inputType === 'autocomplete' && InputComponent && (
              <InputComponent
                apiAuthClient={apiAuthClient}
                allowOpenValueAsItem={allowOpenValueAsItem}
                shouldScrollToView={shouldScrollToView}
                allowOpenValue={allowOpenValue}
                subQueries={autocompleteQuery}
                block={false}
                onFocus={() => setIsFocused(true)}
                onBlur={() => {
                  setIsFocused(false);
                  inputProperties?.onBlur?.();
                }}
                items={autoCompleteItems}
                type={autoCompleteType}
                onSelect={onAdd}
                url={autocompleteUrl}
                onBackSpaceClickWhenInputIsEmpty={() => {
                  if (allowRemoveTagOnBackspaceWhenInputIsEmpty) {
                    const last = values?.[values?.length - 1];
                    if (last) {
                      onRemove?.(last, values?.length - 1);
                    }
                  }
                }}
                makeItemFromInputValue={makeItemFromInputValue}
                inputProps={inputProperties}
                clearOnSelect
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setInputValue(e.target.value);
                }}
                inputValue={inputValue}
                renderItem={renderAutoCompleteItem}
                meta={autoCompleteMeta}
                id={id}
                dropdownHeight={dropdownHeight}
                filterItems={filterAutocompleteItems}
                autoCompleteFetchOptions={autoCompleteFetchOptions}
                extractListFromResponse={extractAutocompleteListFromResponse}
                queryKey={autocompleteQueryKey}
                itemToString={itemToString}
              />
            )}

            {inputType === 'input' && InputComponent && (
              <InputComponent
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setInputValue(e.target.value);
                }}
                id={id}
                value={inputValue}
                onKeyDown={onInputSelect}
                block={false}
                {...inputProperties}
              />
            )}
          </Flex>
        </Flex>
      </Container>
      {maxAllowed && !disableProgressBar && (
        <Flex alignItems={'center'} marginTop={'sm'} className={'progress-container'}>
          <Div flexGrow={1}>
            <ProgressBar
              value={values.length > maxAllowed ? 100 : (values.length / maxAllowed) * 100}
              progressVariant={'emerald'}
              size={'xs'}
            />
          </Div>
          <Text
            aria-live={'polite'}
            variant={maxAllowed === values.length ? 'emerald' : 'gray600'}
            marginLeft={'xs'}
            size={'md'}
            align={'right'}
            weight={500}
          >
            {maxAllowedMessageText || `${values?.length || 0} out of ${maxAllowed}`}
          </Text>
        </Flex>
      )}
    </Div>
  );
};

// this way of typing the styled component allows us to preserve the generic props.
const StyledTagsInput: typeof TagsInputComponent = styled(TagsInputComponent)`
  &.box-tags-input {
    &.grow-input {
      .tags-input-input {
        flex-grow: 1;
      }

      .box-autocomplete {
        flex-grow: 1;
      }
    }

    .input-container {
      position: relative;
      height: auto;
      border-radius: 4px;
      display: flex;
      padding: 4px 4px 0 4px;
      flex-wrap: wrap;

      ${props => variants[props.variant || defaultTagsInputVariant]?.containerStyle};

      input,
      .box-autocomplete {
        min-width: 200px;
        padding-right: ${spacing.xxs};
      }

      .box-autocomplete {
        input[disabled] {
          background: ${props => variants[props.variant || defaultTagsInputVariant]?.disabledInputStyle?.background};
        }
      }

      .group-container {
        flex-grow: 1;

        > * {
          margin-bottom: ${props => variants[props.variant || defaultTagsInputVariant]?.itemPaddingBottom};

          &:not(:only-child):not(:last-child) {
            margin-right: 8px;
          }
        }
      }
    }

    .progress-container {
      width: 100%;
    }
  }
`;

const TagsInput = <T = AutocompleteItem,>(props: TagsInputProps<T>) => <StyledTagsInput {...props} />;

export default TagsInput;
